import { z } from 'zod';

export const PROFILE_MATCHING_CANDIDATE_SOURCES = ['all_candidates', 'positioned_candidates', 'all_and_positioned_candidates'] as const;
export type ProfileMatchingCandidateSource = (typeof PROFILE_MATCHING_CANDIDATE_SOURCES)[number];

export const PROFILE_MATCHING_GENERATION_TYPES = ['basic', 'advanced', 'custom'] as const;
export type ProfileMatchingGenerationType = (typeof PROFILE_MATCHING_GENERATION_TYPES)[number];

export const PROFILE_MATCHING_FILTER_KEYS = [
    'candidateStates',
    'candidateTypes',
    'mobilityAreas',
    'poles',
    'experiences',
    'availabilities',
    'tools',
    'activityAreas',
    'expertiseAreas',
    'languages',
] as const;
export type ProfileMatchingFilterKey = (typeof PROFILE_MATCHING_FILTER_KEYS)[number];

export const PROFILE_MATCHING_FILTER_VALUES_LIMIT = 100;
export const PROFILE_MATCHING_MAX_CANDIDATES = 500;
export const PROFILE_MATCHING_RESULT_PAGE_LIMITS = [10, 20, 50] as const;

const stringArraySchema = z.array(z.string().trim().min(1)).max(PROFILE_MATCHING_FILTER_VALUES_LIMIT).optional();

const profileMatchingDateValueSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const profileMatchingDateFilterSchema = z
    .object({
        period: z.enum(['created', 'updated']),
        startDate: profileMatchingDateValueSchema,
        endDate: profileMatchingDateValueSchema.optional(),
    })
    .strict()
    .refine((value) => !value.endDate || value.endDate >= value.startDate, {
        path: ['endDate'],
        message: 'PROFILE_MATCHING_DATE_FILTER_END_BEFORE_START',
    });

export type ProfileMatchingDateFilter = z.infer<typeof profileMatchingDateFilterSchema>;

export const profileMatchingCandidateFiltersSchema = z
    .object({
        candidateStates: stringArraySchema,
        candidateTypes: stringArraySchema,
        mobilityAreas: stringArraySchema,
        poles: stringArraySchema,
        experiences: stringArraySchema,
        availabilities: stringArraySchema,
        tools: stringArraySchema,
        activityAreas: stringArraySchema,
        expertiseAreas: stringArraySchema,
        languages: stringArraySchema,
        dateFilter: profileMatchingDateFilterSchema.optional(),
    })
    .strict();

export type ProfileMatchingCandidateFilters = z.infer<typeof profileMatchingCandidateFiltersSchema>;

export const profileMatchingResolvedFilterValueSchema = z.object({
    id: z.string(),
    label: z.string(),
});

export const profileMatchingFilterSnapshotSchema = z.object({
    raw: profileMatchingCandidateFiltersSchema,
    resolved: z.record(z.string(), z.array(profileMatchingResolvedFilterValueSchema)),
});

export type ProfileMatchingFilterSnapshot = z.infer<typeof profileMatchingFilterSnapshotSchema>;

export const createProfileMatchingSchema = z
    .object({
        opportunityBoondId: z.string().trim().min(1),
        correlationId: z.string().trim().min(1).optional(),
        candidateSource: z.enum(PROFILE_MATCHING_CANDIDATE_SOURCES).optional(),
        generationType: z.enum(PROFILE_MATCHING_GENERATION_TYPES).optional(),
        candidateFilters: profileMatchingCandidateFiltersSchema.optional(),
        scoringRubric: z.lazy(() => profileMatchingScoringRubricSchema).optional(),
    })
    .strict()
    .superRefine((value, context) => {
        const activeFilterCount = Object.values(value.candidateFilters ?? {}).filter(
            (filterValues) => (Array.isArray(filterValues) ? filterValues.length > 0 : filterValues !== undefined),
        ).length;

        if (value.generationType === 'custom' && activeFilterCount === 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['candidateFilters'],
                message: 'PROFILE_MATCHING_CUSTOM_FILTERS_REQUIRED',
            });
        }

        if (value.generationType !== 'custom' && value.candidateFilters !== undefined) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['candidateFilters'],
                message: 'PROFILE_MATCHING_FILTERS_ONLY_ALLOWED_FOR_CUSTOM',
            });
        }

        if (value.generationType && value.candidateSource) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['candidateSource'],
                message: 'PROFILE_MATCHING_AMBIGUOUS_GENERATION_SOURCE',
            });
        }
    });

export type CreateProfileMatchingBody = z.infer<typeof createProfileMatchingSchema>;

export const PROFILE_MATCHING_RESULT_SORT_FIELDS = ['rank', 'score', 'candidate', 'title'] as const;
export type ProfileMatchingResultSortField = (typeof PROFILE_MATCHING_RESULT_SORT_FIELDS)[number];
export const PROFILE_MATCHING_RESULT_SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type ProfileMatchingResultSortDirection = (typeof PROFILE_MATCHING_RESULT_SORT_DIRECTIONS)[number];
export const PROFILE_MATCHING_RESULT_VISIBILITIES = ['relevant', 'all'] as const;
export type ProfileMatchingResultVisibility = (typeof PROFILE_MATCHING_RESULT_VISIBILITIES)[number];
export const PROFILE_MATCHING_RELEVANT_SCORE_THRESHOLD = 30;

export const findProfileMatchingResultsQuerySchema = z
    .object({
        page: z
            .string()
            .regex(/^[1-9]\d*$/)
            .optional(),
        limit: z.enum(['10', '20', '50']).optional(),
        sort: z.enum(PROFILE_MATCHING_RESULT_SORT_FIELDS).optional(),
        direction: z.enum(PROFILE_MATCHING_RESULT_SORT_DIRECTIONS).optional(),
        visibility: z.enum(PROFILE_MATCHING_RESULT_VISIBILITIES).optional(),
    })
    .strict();

export type FindProfileMatchingResultsQuery = z.infer<typeof findProfileMatchingResultsQuerySchema>;

export const previewCustomProfileMatchingSchema = z
    .object({
        candidateFilters: profileMatchingCandidateFiltersSchema,
    })
    .strict()
    .superRefine((value, context) => {
        const hasActiveFilter = Object.values(value.candidateFilters).some(
            (filterValues) => (Array.isArray(filterValues) ? filterValues.length > 0 : filterValues !== undefined),
        );
        if (!hasActiveFilter) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['candidateFilters'],
                message: 'PROFILE_MATCHING_CUSTOM_FILTERS_REQUIRED',
            });
        }
    });

export type PreviewCustomProfileMatchingBody = z.infer<typeof previewCustomProfileMatchingSchema>;

export const profileMatchingFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
});

export const profileMatchingFilterOptionGroupSchema = z.object({
    group: z.string(),
    items: z.array(profileMatchingFilterOptionSchema),
});

export const profileMatchingFilterOptionItemSchema = z.union([
    profileMatchingFilterOptionSchema,
    profileMatchingFilterOptionGroupSchema,
]);

export type ProfileMatchingFilterOption = z.infer<typeof profileMatchingFilterOptionItemSchema>;

export const profileMatchingFilterOptionsSchema = z.record(z.string(), z.array(profileMatchingFilterOptionItemSchema));
export type ProfileMatchingFilterOptions = z.infer<typeof profileMatchingFilterOptionsSchema>;

export const profileMatchingPreviewSchema = z.object({
    totalCount: z.number().int().nonnegative(),
    exceedsLimit: z.boolean(),
});
export type ProfileMatchingPreview = z.infer<typeof profileMatchingPreviewSchema>;

export const PROFILE_MATCHING_SCORING_RUBRIC_VERSION = 1;
export const PROFILE_MATCHING_SCORING_CRITERION_IMPORTANCES = ['core', 'important', 'bonus'] as const;
export const PROFILE_MATCHING_SCORING_CRITERION_EVIDENCES = ['demonstrated', 'partial', 'absent', 'not-applicable'] as const;

const profileMatchingScoringCriterionKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/);

const profileMatchingScoringCriterionBaseSchema = z
    .object({
        key: profileMatchingScoringCriterionKeySchema,
        label: z.string().trim().min(1).max(160),
        weight: z.number().int().min(1).max(100),
        importance: z.enum(PROFILE_MATCHING_SCORING_CRITERION_IMPORTANCES),
    })
    .strict();

const validateRubricCriteria = (criteria: Array<{ key: string; weight: number }>, context: z.RefinementCtx): void => {
    if (new Set(criteria.map((criterion) => criterion.key)).size !== criteria.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['criteria'],
            message: 'PROFILE_MATCHING_SCORING_CRITERIA_MUST_BE_UNIQUE',
        });
    }

    if (criteria.reduce((total, criterion) => total + criterion.weight, 0) !== 100) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['criteria'],
            message: 'PROFILE_MATCHING_SCORING_WEIGHTS_MUST_TOTAL_100',
        });
    }
};

/** Proposition IA : aucun critère ne peut être rendu indispensable à ce stade. */
export const profileMatchingScoringRubricProposalSchema = z
    .object({
        version: z.literal(PROFILE_MATCHING_SCORING_RUBRIC_VERSION),
        criteria: z.array(profileMatchingScoringCriterionBaseSchema).min(1).max(4),
    })
    .strict()
    .superRefine((value, context) => validateRubricCriteria(value.criteria, context));

export type ProfileMatchingScoringRubricProposal = z.infer<typeof profileMatchingScoringRubricProposalSchema>;

const profileMatchingConfirmedScoringCriterionSchema = profileMatchingScoringCriterionBaseSchema.extend({
    required: z.boolean(),
    requiredScoreCap: z.number().int().min(0).max(100).nullable(),
});

/** Grille confirmée par le recruteur. Un plafond ne concerne qu'un critère explicitement requis. */
export const profileMatchingScoringRubricSchema = z
    .object({
        version: z.literal(PROFILE_MATCHING_SCORING_RUBRIC_VERSION),
        criteria: z.array(profileMatchingConfirmedScoringCriterionSchema).min(1).max(8),
    })
    .strict()
    .superRefine((value, context) => {
        validateRubricCriteria(value.criteria, context);
        value.criteria.forEach((criterion, index) => {
            if (!criterion.required && criterion.requiredScoreCap !== null) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['criteria', index, 'requiredScoreCap'],
                    message: 'PROFILE_MATCHING_SCORE_CAP_REQUIRES_CONFIRMED_REQUIRED_CRITERION',
                });
            }
        });
    });

export type ProfileMatchingScoringRubric = z.infer<typeof profileMatchingScoringRubricSchema>;

export const profileMatchingCriterionAssessmentSchema = z
    .object({
        criterionKey: profileMatchingScoringCriterionKeySchema,
        score: z.number().min(0).max(100),
        evidence: z.enum(PROFILE_MATCHING_SCORING_CRITERION_EVIDENCES),
        justification: z.string().trim().min(1),
    })
    .strict();

export type ProfileMatchingCriterionAssessment = z.infer<typeof profileMatchingCriterionAssessmentSchema>;

/** Compatibilité lecture : présent uniquement dans les matching générés avant la grille générique. */
export const profileMatchingLegacyScorePolicySchema = z.object({
    qaTestExperience: z.enum(['not-required', 'absent', 'partial', 'demonstrated']),
    scoreCap: z.number().min(0).max(100).nullable(),
    penaltyReason: z.string().nullable(),
});

export const profileMatchingResultSchema = z.object({
    rank: z.number().int().positive().optional(),
    boondId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string(),
    score: z.number(),
    justification: z.string(),
    scores: z.record(z.string(), z.number().nullable()).nullable().optional(),
    criterionAssessments: z.array(profileMatchingCriterionAssessmentSchema).nullable().optional(),
    scorePolicy: profileMatchingLegacyScorePolicySchema.nullable().optional(),
});

export type ProfileMatchingResult = z.infer<typeof profileMatchingResultSchema>;

export const profileMatchingPaginatedResultsSchema = z.object({
    count: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    visibleCount: z.number().int().nonnegative(),
    hiddenCount: z.number().int().nonnegative(),
    data: z.array(profileMatchingResultSchema),
});

export type ProfileMatchingPaginatedResults = z.infer<typeof profileMatchingPaginatedResultsSchema>;

export const PositionCandidatesSchema = z.object({
    candidateBoondIds: z.array(z.string()).describe('Les identifiants BoondManager des candidats à positionner'),
});

export type PositionCandidates = z.infer<typeof PositionCandidatesSchema>;

export const SaveKeywordsSchema = z.object({
    keywords: z.array(z.string()).describe('Les mots-clés à sauvegarder pour cette analyse'),
});

export type SaveKeywords = z.infer<typeof SaveKeywordsSchema>;
